<template>
  <div class="cart-sidebar">
    <header class="cart-header">{{ t('title.cart') }}</header>
    <ul v-if="cartStore.cart.CartMenus.length > 0 || menuStore.menus.length > 0" class="cart-item-list">
      <LeftNavCartCartItem
        v-for="(cartMenu, index) in cartStore.cart.CartMenus"
        :key="index"
        :index="index"
        :cart-menu="cartMenu"
      />
    </ul>
    <div v-else class="cart-item-list__empty">{{ t('info.cart_empty') }}</div>
    <footer class="cart-footer">
      <div class="cart-footer__amount">
        <div class="cart-footer__amount-label">{{ t('label.order_amount') }}</div>
        <div class="cart-footer__amount-value">{{ formatCurrency(cartStore.totalAmount) }}</div>
      </div>
      <div class="cart-footer__buttons">
        <BaseButton2 v-if="configStore.usePreOrder" variant="outline" @button-click="handlePaymentClick">
          {{ t('button.pay') }}
        </BaseButton2>
        <BaseButton2 variant="primary" @button-click="handleOrderClick">{{
          configStore.usePayment && !configStore.usePreOrder ? t('button.pay') : t('button.order')
        }}</BaseButton2>
      </div>
    </footer>
  </div>
</template>

<script lang="ts" setup>
  import BaseButton2 from '@/components/base/BaseButton2.vue';
  import OrderComplete from '@/components/OrderComplete.vue';
  import OrderHistory from '@/components/OrderHistory.vue';
  import Payment from '@/components/Payment.vue';
  import LeftNavCartCartItem from '@/components/templates/left-nav-cart/LeftNavCartCartItem.vue';
  import { useCart } from '@/composable/useCart';
  import { useMinOrderQtyValidator } from '@/composable/useMinOrderQtyValidator';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import UserError from '@/error';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency, getNameByLocale } from '@/utils';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const configStore = useConfigStore();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();
  const orderStore = useOrderStore();
  const paymentStore = usePaymentStore();
  const cartStore = useCartStore();

  const { ensureHeadcountAsync } = useCart();
  const { createPaymentSummaryFromCart, createPaymentSplit } = usePayment();
  const { assertMinOrderQty } = useMinOrderQtyValidator();

  /**
   * 주문하기 버튼 클릭
   */
  const handleOrderClick = async () => {
    if (!cartStore.cart.CartMenus || cartStore.cart.CartMenus.length === 0) {
      throw new UserError(t('error.empty_cart'));
    }

    await order();
  };

  /** 결제하기 버튼 클릭 */
  const handlePaymentClick = () => modalStore.openFullScreenModal(OrderHistory, {});

  /**
   * 주문 진행
   */
  const order = async () => {
    const loadingId = commonStore.showLoading();
    try {
      // POS에서 주문 조회 후 세팅
      if ((window as any).AndroidBridge) {
        // ToDo 테스트 코드 삭제
        await orderStore.fetchOrder(false);
      }

      // 최소 주문 금액 검사
      if (configStore.config.IsMinOrderAmountUsed && configStore.config.MinOrderAmount > 0) {
        // 장바구니 금액과 선주문한 금액의 합이 최소주문금액 보다 작은지 검사
        if (cartStore.totalAmount + (orderStore.order.AMT ?? 0) < configStore.config.MinOrderAmount) {
          throw new UserError(t('error.required_min_amount', [formatCurrency(configStore.config.MinOrderAmount)]));
        }
      }

      // 최소 주문 수량 검증
      assertMinOrderQty(cartStore.cart.CartMenus);

      // 장바구니 메뉴 품절 및 한정수량 검사
      await validateCartSoldOut();

      // POS 주문 데이터에 인원 수가 있다면 POS 인원 수로 업데이트
      // 태블릿에 입력되어 있는 경우 POS 인원 수로 덮어쓰기 (1인 1메뉴로 인원 수를 미리 입력한 경우)
      if (orderStore.order.CustomerCnt > 0) {
        cartStore.setHeadcount(orderStore.order.CustomerCnt);
      }

      // 인원 수 유효성 검사 및 조정. 검사 실패 시 주문 프로세스 중단
      if (!(await ensureHeadcountAsync())) return;

      // 1인 1메뉴 옵션을 사용하는 경우, 주메뉴 수량 확인을 위해 POS 주문 데이터 조회
      if (configStore.useOneMenuPerPerson) {
        // 인원수 입력 여부 검사
        if (!cartStore.cart.Headcount) throw new UserError(t('error.required_headcount'));

        // 장바구니 메인메뉴 수량 조회
        const cartMainMenuQty =
          cartStore.cart.CartMenus?.reduce((acc, cartMenu) => {
            return acc + (menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode)?.MainMenuQty || 0) * cartMenu.Qty;
          }, 0) || 0;

        // POS 주문 데이터 메인메뉴 수량 조회
        const orderMainMenuQty =
          orderStore.order.Items?.reduce((acc, item) => {
            return acc + (menuStore.getMenu(null, item.ITEM_CODE)?.MainMenuQty || 0) * item.QTY;
          }, 0) || 0;

        // 인원수와 메인메뉴 수량으로 1인 1메뉴 여부 검사
        if (cartStore.cart.Headcount > cartMainMenuQty + orderMainMenuQty) {
          throw new UserError(t('error.one_menu_per_person'));
        }
      }

      // 결제 기능을 사용하고 결제 금액이 0보다 큰 경우 결제 페이지로 이동. 이후 로직 중단.
      if (configStore.usePayment && !configStore.usePreOrder && cartStore.finalAmount > 0) {
        // 결제 모달 열기
        modalStore.openFullScreenModal(Payment, {
          isPreOrder: false,
          paymentSummary: createPaymentSummaryFromCart(),
        });
        return;
      }

      // 결제 기능을 사용하고 결제 금액이 0인 경우 현금 결제로 세팅
      if (configStore.usePayment && !configStore.usePreOrder && cartStore.finalAmount === 0) {
        // 결제 요약 데이터 세팅
        paymentStore.setSummary(createPaymentSummaryFromCart());
        // 현금 결제 데이터 추가
        paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash));
      }

      // 장바구니 데이터를 주문 데이터에 추가
      orderStore.addOrderItem(cartStore.cart.CartMenus);

      // 주문 전송
      await orderStore.sendOrder(false, false);

      // 주문 완료 모달 창 열기
      modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });

      // 장바구니 모달 창 닫기
      modalStore.closeCartModal();
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };

  /**
   * 장바구니 메뉴 일반 품절, 한정수량 품절 검사
   */
  const validateCartSoldOut = async () => {
    // 실시간 품절 검사 및 한정수량 사용 여부
    const { IsRealtimeSoldOutUsed, IsLimitedItemUsed } = configStore.config;

    // 둘 다 사용하지 않는 경우 검사하지 않는다
    if (!IsRealtimeSoldOutUsed && !IsLimitedItemUsed) return;

    if (IsRealtimeSoldOutUsed) {
      // 일반 품절 데이터 갱신
      await menuStore.fetchSoldOut();
    }

    // 한정수량 옵션 사용하는 경우 한정수량 데이터 갱신
    if (IsLimitedItemUsed) {
      await menuStore.fetchLimitItem();
    }

    // 품절 검사 에러 메시지
    let errorMessage = '';
    for (const cartMenu of cartStore.cart.CartMenus) {
      // 메뉴 상세 데이터 조회
      const menu = menuStore.getMenu(cartMenu.GroupCode, cartMenu.ItemCode);
      const menuName = getNameByLocale(menu);

      if (IsRealtimeSoldOutUsed) {
        // 일반 품절 검사
        if (menu.IsSoldOut) {
          errorMessage += t('error.item_soldout', { name: menuName }) + '\n';
        } else {
          // 토핑 품절 검사
          const toppingNames = cartMenu.Toppings.reduce<string[]>((names, option) => {
            const topping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
            if (topping.IsSoldOut) {
              names.push(getNameByLocale(topping));
            }
            return names;
          }, []).join(' / ');
          if (toppingNames) errorMessage += t('error.item_soldout', { name: `${menuName} - [${toppingNames}]` }) + '\n';

          // 세트 메뉴 품절 검사
          const setMenuNames = cartMenu.SetMenus.reduce<string[]>((names, option) => {
            const setMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
            if (setMenu.IsSoldOut) {
              names.push(getNameByLocale(setMenu));
            }
            return names;
          }, []).join(' / ');
          if (setMenuNames) errorMessage += t('error.item_soldout', { name: `${menuName} - [${setMenuNames}]` }) + '\n';
        }
      }

      // 한정수량 품절 검사 (중복 에레메시지 생성을 막기 위해 메뉴가 품절이 아닌 경우만 검사)
      if (!menu.IsSoldOut && configStore.config.IsLimitedItemUsed) {
        // 현재 메뉴의 한정수량 데이터 조회
        const limitItem = menuStore.getLimitItem(cartMenu.ItemCode);

        // 한정수량 데이터가 존재한다면 수량 검사
        if (limitItem && limitItem.OrderLimitCnt < cartMenu.Qty) {
          if (limitItem.OrderLimitCnt === 0) {
            errorMessage += t('error.item_soldout', { name: menuName }) + '\n';
          } else {
            errorMessage += t('error.limit_item2', { name: menuName, count: limitItem.OrderLimitCnt }) + '\n';
          }
        }
      }
    }

    // 품절 또는 한정수량 메뉴가 있다면 에러 메시지 표시
    if (errorMessage) {
      throw new UserError(errorMessage.trim());
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .cart-sidebar {
    display: grid;
    grid-template-rows: vars.$left-nav-header-height 1fr auto;
    width: 100%;
    height: 100%;
    padding: 0 0.7813vw;
    background-color: var(--sidebar-bg);

    .cart-header {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      font-size: 1.875vw;
      font-weight: bold;
      color: var(--text-contrast-color);
    }

    .cart-item-list {
      padding-bottom: 0.7813vw;
      list-style: none;
      overflow-y: auto;

      &__empty {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        font-size: 1.5625vw;
        color: var(--text-contrast-color);
      }

      li:not(:last-child) {
        border-bottom: 1px solid #e5e5e5; /* 구분선 스타일 */
      }
    }

    .cart-footer {
      display: flex;
      flex-direction: column;
      padding-bottom: 0.7813vw;
      border-top: 0.1563vw solid var(--header-vertical-border);

      &__amount {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.7813vw 0;
        font-weight: bold;

        &-label {
          font-size: 1.8657vw;
          color: var(--text-contrast-color);
        }

        &-value {
          font-size: 2.3881vw;
          color: var(--point-color-secondary);
        }
      }

      &__buttons {
        display: flex;
        gap: 0.7813vw;
        justify-content: center;

        button {
          width: 100%;
        }
      }
    }
  }
</style>
